/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { FeatureCode, FeatureDetails, getDefaultScopePacks, ScopePackDataScope } from '@g17eco/core';
import { RootState } from '../../reducers';
import { RootConfig } from '../../types/initiative';

interface LimitCheck {
  current: number,
  limit: number
}

export class FeaturePermissions {
  private static get(state: RootState, featureCode: FeatureCode): FeatureDetails | undefined {
    const config = state.globalData.loaded ? state.globalData.data.config : undefined;
    if (!config) {
      return;
    }
    return config.features?.find(f => f.code === featureCode);
  }

  private static canAccess(state: RootState, featureCode: FeatureCode): boolean {
    return Boolean(FeaturePermissions.get(state, featureCode));
  }

  private static getLimit(state: RootState, featureCode: FeatureCode): number | undefined {
    return FeaturePermissions.get(state, featureCode)?.config?.limit;
  }

  // Feature toggles
  public static canAccessAdminDashboard(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.AdminDashboard);
  }
  public static canAccessAssurance(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.Assurance);
  }
  public static canAccessBulkDelegation(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.BulkDelegation);
  }
  public static canAccessBulkImporting(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.BulkImporting);
  }

  public static canAccessCombinedReport(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.CombinedReport);
  }
  public static canAccessCombinedSurvey(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.CombinedSurvey);
  }
  public static canAccessSurveyTemplates(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.SurveyTemplates);
  }
  public static canAccessVerification(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.Verification);
  }
  public static canAccessDownloads(state: RootState) {
    // We used to have this, so added a stub rather than remove the logic in the code in case we want to re-add
    return true;
  }
  public static canAccessCustomDashboards(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.CustomDashboards);
  }

  public static canSetNoteInstructions(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.NoteInstructions);
  }

  public static canAccessAITools(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.AITools);
  }

  public static canAccessScopePacks(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.ScopePacks);
  }

  public static canUseRichTextEditor(state: RootState) {
    return FeaturePermissions.canAccess(state, FeatureCode.RichTextEditor);
  }

  // Feature limits
  public static getLimitCustomMetric(state: RootState) {
    return FeaturePermissions.getLimit(state, FeatureCode.CustomMetrics) ?? 0;
  }
  public static getLimitReportingLevels(state: RootState) {
    return FeaturePermissions.getLimit(state, FeatureCode.ReportingLevels) ?? 1;
  }
  public static getLimitUsers(state: RootState) {
    return FeaturePermissions.getLimit(state, FeatureCode.Users) ?? 1;
  }
  public static getLimitSurveys(state: RootState) {
    return -1; // must be below 0 to considered as No limit
  }

  public static isBelowLimit({ current, limit }: LimitCheck) {
    return limit < 0 || current < limit;
  }

  public static hasReachedLimit(check: LimitCheck) {
    return !FeaturePermissions.isBelowLimit(check);
  }

  /** Check what is supported in scope selection page **/
  public static getScopePackFeature(state: RootState): ScopePackDataScope {
    const scopePacks = FeaturePermissions.get(state, FeatureCode.ScopePacks);

    return scopePacks?.config?.scopePacks ?? getDefaultScopePacks();
  }

  public static getConfigLimit(config: RootConfig, featureCode: FeatureCode, defaultLimit: 0) {
    return config.features?.find(f => f.code === featureCode)?.config?.limit ?? defaultLimit;
  }
}
